import Box from '@mui/material/Box'

/**
 * The marks a Tamil calendar carries, drawn rather than written.
 *
 * <p>A month of dates has to be read at a glance from across a desk, and the
 * three things that decide whether the halls fill are the muhurtham, the full
 * moon and the new moon. Drawn as shapes they are taken in without reading;
 * spelled out they would not fit in a day cell at all.</p>
 *
 * <p>They are drawn here rather than typed as characters because a font decides
 * how a character looks, and the two calendars sit on screens with different
 * fonts. A shape is the same shape everywhere.</p>
 */

/** Everything scales with the type around it, so one size fits both calendars. */
const MARK_SIZE = '1.05em'

/**
 * The gold a muhurtham is marked in, dark enough to be seen on white.
 *
 * <p>Gold as a screen colour is nearly invisible on a white cell; this is the
 * metal as it is drawn rather than as it is named.</p>
 */
export const MUHURTHAM_GOLD = '#C9992B'

/**
 * The mark for a day the almanac says a wedding may be fixed on.
 *
 * <p>Two letters rather than a drawing. A thali at the size of a calendar cell
 * is a shape somebody has to be told the meaning of once and remember; "Mu" is
 * read.</p>
 */
export function MuhurthamMark({ title }: { title?: string }) {
  return (
    <Box
      component="span"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      sx={{
        display: 'block',
        flexShrink: 0,
        fontWeight: 700,
        // Set against the type around it, like the moons beside it, so one
        // size fits both calendars.
        fontSize: '0.95em',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      }}
    >
      Mu
    </Box>
  )
}

/**
 * The moon on a given night: white and whole at Pournami, dark at Amavasai.
 *
 * <p>Drawn as the moon actually looks rather than as two similar characters -
 * the full one is the lit disc, the new one is the unlit disc - so nobody has
 * to remember which ring meant which.</p>
 */
export function MoonMark({ phase, title }: { phase: 'POURNAMI' | 'AMAVASAI'; title?: string }) {
  const full = phase === 'POURNAMI'
  return (
    <Box
      component="svg"
      viewBox="0 0 12 12"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      sx={{ width: MARK_SIZE, height: MARK_SIZE, display: 'block', flexShrink: 0 }}
    >
      <circle
        cx="6"
        cy="6"
        r="4"
        // The full moon is white, so it needs its own outline to be seen at
        // all on paper; the new moon is the dark disc itself.
        fill={full ? '#ffffff' : 'currentColor'}
        stroke="currentColor"
        strokeWidth={full ? 1.1 : 0.8}
      />
    </Box>
  )
}
